import { useEffect, useState } from "react";
import { BadgeCheck, Link2, RefreshCw, Sparkles } from "lucide-react";
import {
  ApiError,
  api,
  type ExternalVerificationResult,
  type SkillClaim,
  type SkillRecommendation,
} from "../lib/api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";

/** Skills: normalized claims, wallet-verified status, external verify + recommender (M5). */
export function SkillsPage() {
  const [claims, setClaims] = useState<SkillClaim[] | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    void api.listSkills().then((r) => setClaims(r.claims));
  }
  useEffect(refresh, []);

  async function rematch() {
    setBusy(true);
    try {
      await api.rematchSkills();
      refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Skills</h1>
        <Button variant="secondary" size="sm" onClick={() => void rematch()} disabled={busy}>
          <RefreshCw size={14} /> Re-check wallet
        </Button>
      </div>

      <ExternalVerify />

      {!claims ? (
        <div className="h-40 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />
      ) : claims.length === 0 ? (
        <Card>
          <CardContent className="grid place-items-center gap-2 p-12 text-center">
            <Sparkles size={32} className="text-muted-foreground" />
            <p className="font-medium">No skills yet</p>
            <p className="text-sm text-muted-foreground">
              Upload a resume and we&apos;ll normalize your skills and match them to your verified credentials.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {claims.map((claim) => (
            <SkillRow key={claim.id} claim={claim} />
          ))}
        </div>
      )}
    </div>
  );
}

function SkillRow({ claim }: { claim: SkillClaim }) {
  const [recs, setRecs] = useState<SkillRecommendation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const label = claim.label ?? claim.rawText;

  async function getVerified() {
    setLoading(true);
    try {
      const r = await api.skillRecommendations(claim.id);
      setRecs(r.recommendations);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{label}</p>
            {claim.label && claim.label.toLowerCase() !== claim.rawText.toLowerCase() && (
              <p className="truncate text-xs text-muted-foreground">from &ldquo;{claim.rawText}&rdquo;</p>
            )}
            {!claim.label && <p className="text-xs text-muted-foreground">unmatched — no taxonomy hit</p>}
          </div>
          {claim.verified ? (
            <Badge variant="verified">
              <BadgeCheck size={12} /> Verified
            </Badge>
          ) : (
            <Badge variant="claimed">Claimed</Badge>
          )}
          {!claim.verified && (
            <Button variant="ghost" size="sm" onClick={() => void getVerified()} disabled={loading}>
              <Sparkles size={14} /> Get verified
            </Button>
          )}
        </div>
        {recs && (
          <div className="rounded-md bg-secondary p-3 text-sm">
            {recs.length === 0 ? (
              <p className="text-muted-foreground">No recommendations available yet.</p>
            ) : (
              <>
                <p className="mb-1 font-medium">Credentials that would verify this:</p>
                <ul className="list-inside list-disc text-muted-foreground">
                  {recs.map((r) => (
                    <li key={r.canonicalId}>
                      {r.label} <span className="text-xs">({Math.round(r.confidence * 100)}%)</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExternalVerify() {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<ExternalVerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!url.trim()) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await api.verifyExternal(url.trim());
      setResult(r.result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verify an external credential</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Paste a public Credly or Accredible link to verify a credential issued elsewhere.
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="https://www.credly.com/badges/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button onClick={() => void submit()} disabled={busy}>
            <Link2 size={14} /> Verify
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {result &&
          (result.ok ? (
            <div className="rounded-md bg-[var(--color-success-subtle)] p-3 text-sm">
              <p className="font-medium text-[var(--color-success)]">
                <BadgeCheck size={14} className="mr-1 inline" /> Verified via {result.provider}
              </p>
              <p>{result.title}</p>
              {result.issuer && <p className="text-muted-foreground">Issued by {result.issuer}</p>}
            </div>
          ) : (
            <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Could not verify ({result.error ?? "unknown error"}).
            </p>
          ))}
      </CardContent>
    </Card>
  );
}
